import { z } from 'zod';
import { ALL_BR_TAX_REGIMES } from './enums';

/**
 * Dados fiscais da PJ brasileira que vende pelo Asaas e emite NFS-e, guardados
 * na entidade vendedora (`/config/sellerEntities/{id}.br_fiscal`).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: "quero poder editar no admin
 * `/config/sellerEntities/etiquetachef-br` todos os dados da empresa BR
 * necessários para cobrar e emitir nota", owner).
 *
 * Por quê: até a A7 a config da nota morava solta em
 * `config/billing/settings/asaas_nfse_{live|sandbox}`, escrita à mão pelo
 * runbook, e os dados da empresa (CCM, CNAE, regime, NBS) só existiam no painel
 * do Asaas. Ninguém os editava pelo admin, e a conta de produção nasceu sem
 * eles. Agora a entidade é a fonte: o admin edita, a emissão lê daqui
 * (`billing/asaas/nfse.ts`, `loadAsaasNfseConfig`), e o botão "Enviar ao Asaas"
 * (`adminSyncAsaasFiscalInfo`) copia a parte da conta para `POST /v3/fiscalInfo`.
 *
 * Contrato: o **certificado A1 nunca passa por aqui** — é enviado no painel do
 * Asaas; a entidade só guarda o que não é segredo. Percentuais em pontos
 * (2 = 2 %).
 *
 * Usado por: `SellerEntitySchema.br_fiscal` (`schemas.ts`); Functions —
 * `billing/asaas/nfse.ts`, `admin/sellerEntities.ts`; admin — editor de
 * entidades vendedoras (`apps/admin/src/features/sellerEntities/`).
 *
 * Dicas: o NBS vai **com pontos** (`1.1103.21.00`); sem pontos o Asaas recusa
 * (medido no sandbox, `docs/billing/asaas-nfse.md`). O NBS `1.1502.20.00` do
 * spike é de desenvolvimento personalizado e NÃO serve à EtiquetaChef (#756,
 * checklist fiscal de 2026-10-01).
 *
 * Issues: #754 (A5, a emissão), #756 (A7, a entidade BR).
 */

/**
 * Configuração da emissão da NFS-e por modo do Asaas: o que vai em cada
 * `POST /v3/invoices`.
 *
 * Origem: #754 — 2026-09-27 22:26 +0200 (A5 do Asaas, em
 * `apps/functions/src/billing/asaas/nfse.ts`); movido para cá na #756 —
 * 2026-10-01 11:33 +0200, para o admin validar com o mesmo schema que a emissão.
 *
 * Por quê: o serviço municipal e os impostos são da CONTA do Asaas (sandbox e
 * produção têm ids diferentes) e decisão do contador — não cabem no código.
 *
 * Contrato: `municipal_service_id` OU `municipal_service_code` (depende da
 * prefeitura; `GET /invoices/municipalServices` lista os ids).
 *
 * Usado por: `SellerEntityBrFiscalSchema.nfse`; Functions —
 * `loadAsaasNfseConfig` (`billing/asaas/nfse.ts`).
 *
 * Dados (sandbox, 2026-10-01):
 *   { "service_description": "Assinatura EtiquetaChef", "observations": "",
 *     "municipal_service_id": "104",
 *     "municipal_service_name": "02798 | 1.05 - Licenciamento ou cessão de direito de uso de programas de computação…",
 *     "taxes": { "retainIss": false, "iss": 2, "pis": 0, "cofins": 0, "csll": 0, "inss": 0, "ir": 0 } }
 */
export const AsaasNfseConfigSchema = z
  .object({
    /**
     * Texto FIXO da nota — desde a #756 (2026-10-07) só o fallback: a emissão
     * monta "Licença de uso do software EtiquetaChef – assinatura <ciclo> com
     * início em … e término em …" (`billing/asaas/nfseDescription.ts`) e usa
     * este texto quando não consegue afirmar o período.
     */
    service_description: z.string().min(1).max(500),
    /** Obrigatório na API do Asaas; vazio é aceito. */
    observations: z.string().max(1000).default(''),
    municipal_service_id: z.string().min(1).max(64).optional(),
    municipal_service_code: z.string().min(1).max(64).optional(),
    municipal_service_name: z.string().min(1).max(500),
    taxes: z
      .object({
        retainIss: z.boolean(),
        iss: z.number().min(0).max(100),
        pis: z.number().min(0).max(100),
        cofins: z.number().min(0).max(100),
        csll: z.number().min(0).max(100),
        inss: z.number().min(0).max(100),
        ir: z.number().min(0).max(100),
      })
      .loose(),
  })
  // Bug corrigido em 2026-10-07 18:05 +0200 (#756): exigia "pelo menos um", e
  // o editor aceitou `municipal_service_code: "02800 | 1.05"` (o NOME da lista,
  // digitado no campo de código) junto do id `102171`. A API do Asaas pede um
  // OU outro, e a emissão mandava os dois. Agora é exatamente um; o admin
  // escolhe o serviço num dropdown da lista do Asaas e o servidor confere o id
  // contra essa lista (`assertFiscalCatalog`, `admin/sellerEntities.ts`).
  .refine((c) => (c.municipal_service_id !== undefined) !== (c.municipal_service_code !== undefined), {
    message: 'informe o serviço municipal pelo id OU pelo código — exatamente um',
    path: ['municipal_service_id'],
  });
export type AsaasNfseConfig = z.infer<typeof AsaasNfseConfigSchema>;

/** NBS com pontos, ex.: `1.1103.21.00` (sem pontos o Asaas recusa). */
export const NBS_CODE_REGEX = /^\d\.\d{4}\.\d{2}\.\d{2}$/;

/**
 * Bloco `br_fiscal` da entidade vendedora brasileira.
 *
 * Dados (ilustrativo, valores fictícios):
 *   { "municipal_registration": "00000000", "state_registration": null,
 *     "cnae": "6203100", "tax_regime": "SIMPLES_NACIONAL",
 *     "special_tax_regime": null, "national_portal_tax_calculation_regime": null,
 *     "nbs_code": "1.1103.21.00", "fiscal_email": "fiscal@example.com",
 *     "rps_serie": "1", "rps_number": 1, "lote_number": 1,
 *     "address_number": "100", "address_complement": null,
 *     "district": "Centro", "ibge_city_code": "3550308",
 *     "nfse": { "live": null, "sandbox": { …AsaasNfseConfig } } }
 *
 * Campos que importam: `nfse.live` nulo = a NFS-e de produção não sai (alerta
 * `asaas_nfse_config_missing:live`), e o `setMarketConfig` recusa ASAAS live
 * no BR; `cnae` só dígitos (7); `ibge_city_code` 7 dígitos.
 */
export const SellerEntityBrFiscalSchema = z.object({
  /** CCM / inscrição municipal. */
  municipal_registration: z.string().trim().min(1).max(30),
  state_registration: z.string().trim().max(30).nullable(),
  /** CNAE principal, só dígitos (ex.: 6203100). */
  cnae: z.string().regex(/^\d{7}$/),
  tax_regime: z.enum(ALL_BR_TAX_REGIMES),
  /** Código do regime especial no Asaas (`specialTaxRegime`), do contador. */
  special_tax_regime: z.string().trim().max(30).nullable(),
  /** `nationalPortalTaxCalculationRegime` do Asaas, do contador. */
  national_portal_tax_calculation_regime: z.string().trim().max(60).nullable(),
  nbs_code: z.string().regex(NBS_CODE_REGEX).nullable(),
  fiscal_email: z.email(),
  rps_serie: z.string().trim().max(10).nullable(),
  rps_number: z.number().int().nonnegative().nullable(),
  lote_number: z.number().int().nonnegative().nullable(),
  address_number: z.string().trim().min(1).max(20),
  address_complement: z.string().trim().max(100).nullable(),
  district: z.string().trim().min(1).max(100),
  ibge_city_code: z.string().regex(/^\d{7}$/),
  nfse: z.object({
    live: AsaasNfseConfigSchema.nullable(),
    sandbox: AsaasNfseConfigSchema.nullable(),
  }),
});
export type SellerEntityBrFiscal = z.infer<typeof SellerEntityBrFiscalSchema>;
